import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import CadDocumentCreator from '../../components/CadDocumentCreator/CadDocumentCreator';
import './Documents.css';

import API_URL from '../../config';

const TYPE_TABS = [
  { key: '',            label: 'Tümü' },
  { key: 'regulation',  label: 'Yönetmelik' },
  { key: 'standard',    label: 'Standart' },
  { key: 'publication', label: 'Yayın' },
  { key: 'template_doc', label: 'Şablon Dok.' },
  { key: 'cad_template', label: 'CAD' },
  { key: 'community',   label: 'Topluluk' },
];

const TYPE_LABELS = {
  regulation: 'Yönetmelik',
  standard: 'Standart',
  publication: 'Yayın',
  template_doc: 'Şablon Dok.',
  cad_template: 'CAD',
  community: 'Topluluk',
};

const DISCIPLINE_OPTIONS = [
  { value: '', label: 'Tüm Disiplinler' },
  { value: 'electrical', label: 'Elektrik' },
  { value: 'fire', label: 'Yangın' },
  { value: 'weak', label: 'Zayıf Akım' },
];

const Documents = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCadCreator, setShowCadCreator] = useState(false);
  const [creating, setCreating] = useState(false);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');

  // Create form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState('regulation');
  const [formDiscipline, setFormDiscipline] = useState('electrical');
  const [formSourceUrl, setFormSourceUrl] = useState('');
  const [formVersion, setFormVersion] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formFile, setFormFile] = useState(null);

  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const token = localStorage.getItem('projabze_token');

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeType) params.set('type', activeType);
      if (discipline) params.set('discipline', discipline);
      if (searchQuery.trim()) params.set('q', searchQuery.trim());

      const res = await fetch(`${API_URL}/api/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const list = data.documents || data;
      setDocuments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Documents fetch error:', err);
    }
    setLoading(false);
  }, [token, activeType, discipline, searchQuery]);

  useEffect(() => {
    if (token) {
      fetchDocuments();
    }
  }, [token, activeType, discipline]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchDocuments();
    }, 400);
  };

  // Trigger search fetch when searchQuery changes via debounce
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formTitle.trim() || !formFile || creating) return;
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('file', formFile);
      formData.append('title', formTitle);
      formData.append('description', formDescription);
      formData.append('type', formType);
      formData.append('discipline', formDiscipline);
      if (formSourceUrl.trim()) formData.append('sourceUrl', formSourceUrl);
      if (formVersion.trim()) formData.append('version', formVersion);
      if (formTags.trim()) formData.append('tags', formTags);

      const res = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setDocuments(prev => [data.document || data, ...prev]);
        resetForm();
        setShowCreateModal(false);
      }
    } catch (err) {
      console.error('Document create error:', err);
    }
    setCreating(false);
  };

  const resetForm = () => {
    setFormTitle('');
    setFormDescription('');
    setFormType('regulation');
    setFormDiscipline('electrical');
    setFormSourceUrl('');
    setFormVersion('');
    setFormTags('');
    setFormFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Bu dokümanı silmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleSubscribe = async (docId, isSubscribed) => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}/subscribe`, {
        method: isSubscribed ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDocuments(prev => prev.map(d =>
          d.id === docId ? { ...d, isSubscribed: !isSubscribed } : d
        ));
      }
    } catch (err) {
      console.error('Subscribe error:', err);
    }
  };

  const handleOutdatedVote = async (docId) => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}/outdated-vote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setDocuments(prev => prev.map(d =>
          d.id === docId
            ? {
                ...d,
                outdatedVotes: data.outdatedVotes ?? (d.outdatedVotes || 0) + (data.voted ? 1 : -1),
                hasVotedOutdated: data.voted ?? !d.hasVotedOutdated
              }
            : d
        ));
      }
    } catch (err) {
      console.error('Outdated vote error:', err);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Az önce';
    if (mins < 60) return `${mins}dk`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}sa`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}g`;
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getTypeClass = (type) => {
    const map = {
      regulation: 'badge-regulation',
      standard: 'badge-standard',
      publication: 'badge-publication',
      template_doc: 'badge-template',
      community: 'badge-community',
    };
    return map[type] || '';
  };

  if (!user) return null;

  return (
    <div className={`documents-page ${theme}`}>
      <Topbar user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }} />
      <Sidebar activeId="documents" user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }} />

      <main className="docs-main">
        {/* Header */}
        <div className="docs-header">
          <div className="docs-header-left">
            <h1 className="docs-title">Dokümanlar</h1>
            <p className="docs-subtitle">Yönetmelik, standart ve teknik dokümanlar</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="docs-create-btn docs-create-btn-cad" onClick={() => setShowCadCreator(true)} title="CAD doküman oluştur (Başlık vb.)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="9" x2="9" y2="21"/>
              </svg>
              CAD +
            </button>
            <button className="docs-create-btn" onClick={() => setShowCreateModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Yeni Doküman Yükle
            </button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="docs-tabs">
          {TYPE_TABS.map(tab => (
            <button
              key={tab.key}
              className={`docs-tab ${activeType === tab.key ? 'active' : ''}`}
              onClick={() => setActiveType(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="docs-filter-bar">
          <div className="docs-filter-select-wrap">
            <select
              className="docs-filter-select"
              value={discipline}
              onChange={e => setDiscipline(e.target.value)}
            >
              {DISCIPLINE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <svg className="docs-filter-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          <div className="docs-search-wrap">
            <svg className="docs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className="docs-search-input"
              placeholder="Doküman ara... (başlık, etiket, açıklama)"
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button className="docs-search-clear" onClick={() => { setSearchQuery(''); fetchDocuments(); }}>
                ×
              </button>
            )}
          </div>
        </div>

        {/* Document Cards */}
        <div className="docs-list">
          {loading && <div className="docs-loading">Yükleniyor...</div>}

          {!loading && documents.length === 0 && (
            <div className="docs-empty">
              <div className="docs-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h3>Doküman bulunamadı</h3>
              <p>Arama kriterlerinize uygun doküman yok veya henüz doküman yüklenmemiş.</p>
            </div>
          )}

          {!loading && documents.map(doc => (
            <div className={`docs-card ${doc.isOutdated ? 'docs-card-outdated' : ''}`} key={doc.id}>
              <div className="docs-card-top">
                <div className="docs-card-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <div className="docs-card-info">
                  <div className="docs-card-title-row">
                    <h3 className="docs-card-title">{doc.title}</h3>
                    {doc.isOutdated && (
                      <span className="docs-outdated-badge">Güncel Değil</span>
                    )}
                  </div>
                  <div className="docs-card-meta">
                    <span className={`docs-type-badge ${getTypeClass(doc.type)}`}>
                      {TYPE_LABELS[doc.type] || doc.type}
                    </span>
                    {doc.version && <span className="docs-version">v{doc.version}</span>}
                    {doc.discipline && (
                      <span className="docs-discipline">
                        {DISCIPLINE_OPTIONS.find(o => o.value === doc.discipline)?.label || doc.discipline}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {doc.description && (
                <p className="docs-card-desc">{doc.description}</p>
              )}

              {doc.tags && doc.tags.length > 0 && (
                <div className="docs-card-tags">
                  {(Array.isArray(doc.tags) ? doc.tags : doc.tags.split(',')).map((tag, i) => (
                    <span className="docs-tag" key={i}>{tag.trim()}</span>
                  ))}
                </div>
              )}

              <div className="docs-card-footer">
                <div className="docs-card-author">
                  <div className="docs-card-avatar">
                    {doc.author?.avatar
                      ? <img src={API_URL + doc.author.avatar} alt="" />
                      : (doc.author?.firstName?.[0] || '') + (doc.author?.lastName?.[0] || '')
                    }
                  </div>
                  <span className="docs-card-author-name">
                    {doc.author?.firstName} {doc.author?.lastName}
                  </span>
                  <span className="docs-card-time">{formatTime(doc.createdAt)}</span>
                </div>

                <div className="docs-card-stats">
                  {doc.downloadCount != null && (
                    <span className="docs-stat">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      {doc.downloadCount}
                    </span>
                  )}
                </div>
              </div>

              <div className="docs-card-actions">
                <button
                  className={`docs-action-btn docs-outdated-btn ${doc.hasVotedOutdated ? 'voted' : ''}`}
                  onClick={() => handleOutdatedVote(doc.id)}
                  title="Güncel değil olarak işaretleyin"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Güncel Değil
                  {(doc.outdatedVotes != null && doc.outdatedVotes > 0) && (
                    <span className="docs-vote-count">{doc.outdatedVotes}</span>
                  )}
                </button>

                <button
                  className={`docs-action-btn docs-subscribe-btn ${doc.isSubscribed ? 'subscribed' : ''}`}
                  onClick={() => handleSubscribe(doc.id, doc.isSubscribed)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={doc.isSubscribed ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {doc.isSubscribed ? 'Takipte' : 'Takip Et'}
                </button>

                {doc.fileUrl && (
                  <a
                    className="docs-action-btn docs-download-btn"
                    href={`${API_URL}${doc.fileUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    İndir
                  </a>
                )}

                {doc.sourceUrl && (
                  <a
                    className="docs-action-btn docs-source-btn"
                    href={doc.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    Kaynak
                  </a>
                )}

                {doc.userId === user.id && (
                  <button className="docs-action-btn docs-delete-btn" onClick={() => handleDelete(doc.id)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    Sil
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <RightSidebar />

      {/* CAD Doküman Oluşturucu */}
      {showCadCreator && (
        <CadDocumentCreator
          onClose={() => setShowCadCreator(false)}
          onCreated={(doc) => {
            setDocuments(prev => [doc, ...prev]);
            setShowCadCreator(false);
          }}
          apiUrl={API_URL}
          token={token}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="docs-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="docs-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h2>Yeni Doküman Yükle</h2>
              <button className="docs-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <form className="docs-modal-form" onSubmit={handleCreate}>
              <div className="docs-form-group">
                <label>Başlık *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="Doküman başlığı"
                  required
                />
              </div>

              <div className="docs-form-group">
                <label>Açıklama</label>
                <textarea
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                  placeholder="Doküman hakkında kısa açıklama..."
                  rows={3}
                />
              </div>

              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label>Tür *</label>
                  <select value={formType} onChange={e => setFormType(e.target.value)}>
                    <option value="regulation">Yönetmelik</option>
                    <option value="standard">Standart</option>
                    <option value="publication">Yayın</option>
                    <option value="template_doc">Şablon Dok.</option>
                    <option value="community">Topluluk</option>
                  </select>
                </div>
                <div className="docs-form-group">
                  <label>Disiplin *</label>
                  <select value={formDiscipline} onChange={e => setFormDiscipline(e.target.value)}>
                    <option value="electrical">Elektrik</option>
                    <option value="fire">Yangın</option>
                    <option value="weak">Zayıf Akım</option>
                  </select>
                </div>
              </div>

              <div className="docs-form-row">
                <div className="docs-form-group">
                  <label>Kaynak URL</label>
                  <input
                    type="url"
                    value={formSourceUrl}
                    onChange={e => setFormSourceUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="docs-form-group">
                  <label>Versiyon</label>
                  <input
                    type="text"
                    value={formVersion}
                    onChange={e => setFormVersion(e.target.value)}
                    placeholder="örn. 1.0"
                  />
                </div>
              </div>

              <div className="docs-form-group">
                <label>Etiketler</label>
                <input
                  type="text"
                  value={formTags}
                  onChange={e => setFormTags(e.target.value)}
                  placeholder="virgül ile ayırın: elektrik, tesisat, yönetmelik"
                />
              </div>

              <div className="docs-form-group">
                <label>Dosya *</label>
                <div
                  className={`docs-file-drop ${formFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.zip,.rar,.png,.jpg,.jpeg"
                    onChange={e => setFormFile(e.target.files?.[0] || null)}
                  />
                  {formFile ? (
                    <div className="docs-file-selected">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span>{formFile.name}</span>
                      <button type="button" className="docs-file-remove" onClick={e => { e.stopPropagation(); setFormFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="docs-file-placeholder">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span>Dosya seçmek için tıklayın</span>
                      <span className="docs-file-hint">PDF, DOC, DOCX, XLS, DWG, DXF, ZIP</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="docs-modal-actions">
                <button type="button" className="docs-modal-cancel" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit" className="docs-modal-submit" disabled={creating || !formTitle.trim() || !formFile}>
                  {creating ? 'Yükleniyor...' : 'Yükle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
